<template>
  <!-- 移动端搜索面板：顶栏圆角输入框 + 取消；空输入态展示最近/热门搜索，非空态展示结果列表
       搜索逻辑与 PC 端导航搜索完全一致（同一 API、同一历史存储键、同一防抖与上报策略） -->
  <div class="m-search">
    <!-- 输入栏：渐变描边胶囊输入框（同 PC 搜索框描边色系）+ 取消 -->
    <div class="m-search__bar">
      <div class="m-search__inputwrap" :class="{ 'm-search__inputwrap--filled': hasKeyword }">
        <span class="m-search__icon"></span>
        <input ref="input" v-model="keyword" class="m-search__input" type="text"
          :placeholder="panelPlaceholder" @keydown.enter="goSearchPage" />
        <img v-if="hasKeyword" class="m-search__clear" :src="img('search_close.svg')" alt="清空输入"
          @click="keyword = ''" />
      </div>
      <button class="m-search__cancel" type="button" @click="$emit('close')">取消</button>
    </div>

    <div class="m-search__body">
      <!-- 空输入态：最近搜索（有历史时）+ 热门搜索 -->
      <template v-if="!hasKeyword">
        <div v-if="showRecent" class="m-search__section">
          <div class="m-search__sec-head">
            <div class="m-search__sec-title">
              <img :src="img('search_clock.svg')" alt="" />
              <span>最近搜索</span>
            </div>
            <div class="m-search__clearall" @click="clearRecent">
              <img :src="img('search_trash.svg')" alt="" />
              <span>清空</span>
            </div>
          </div>
          <div class="m-search__tags">
            <span v-for="word in recentSearches" :key="word" class="m-search__tag" @click="pickRecent(word)">{{
              shortRecent(word) }}</span>
          </div>
        </div>

        <div class="m-search__section">
          <div class="m-search__sec-head">
            <div class="m-search__sec-title">
              <img :src="img('flame_icon.svg')" alt="" />
              <span>热门搜索</span>
            </div>
          </div>
          <ul class="m-search__hot-list">
            <li v-for="(item, i) in hotWords" :key="item.keyword" @click="pickHot(item.keyword)">
              <em :style="{ color: hotRankColor(i) }">{{ i + 1 }}</em>
              <span>{{ item.keyword }}</span>
            </li>
          </ul>
        </div>
      </template>

      <!-- 非空输入态：搜索结果列表 / 暂无搜索结果 -->
      <template v-else>
        <ul v-if="searchResults.length" class="m-search__results">
          <li v-for="item in searchResults" :key="item.id" @click="onResultClick(item)">
            <p class="m-search__result-title">
              <template v-for="(seg, si) in highlightSegs(item.title)">
                <span v-if="seg.hit" :key="'t' + si" class="m-search__hl">{{ seg.t }}</span>
                <template v-else>{{ seg.t }}</template>
              </template>
            </p>
            <p class="m-search__result-desc">
              <template v-for="(seg, si) in highlightSegs(item.desc)">
                <span v-if="seg.hit" :key="'d' + si" class="m-search__hl">{{ seg.t }}</span>
                <template v-else>{{ seg.t }}</template>
              </template>
            </p>
          </li>
        </ul>
        <div v-else-if="searched" class="m-search__empty">暂无搜索结果</div>
      </template>
    </div>
  </div>
</template>

<script>
import api from "@/api/index";

const ASSETS = "home/";
// 最近搜索本地存储键（与 PC 端共用，双端历史互通）
const RECENT_KEY = "tushare_search_history";
const RECENT_MAX = 10;
// 最近搜索标签最长展示字数，超出以「…」省略
const RECENT_SHOW_MAX = 14;
// 实时搜索防抖时长（同 PC）
const SEARCH_DEBOUNCE = 1000;

export default {
  name: "H5SearchPanel",
  data() {
    return {
      keyword: "",
      // 最近搜索（localStorage 持久化，最新在前，上限 10 条）
      recentSearches: [],
      // 热门搜索词（GET /documents/search/hot_words）
      hotWords: [],
      // 搜索结果（GET /documents/search）
      searchResults: [],
      // 本次搜索结果对应的关键词（用于高亮，避免与输入框实时值错位）
      resultKeyword: "",
      // 当前搜索的 search_id（点击结果时回传给 /documents/search/select）
      searchId: "",
      // 是否已发起过搜索（控制「暂无搜索结果」只在请求返回后展示）
      searched: false,
    };
  },
  computed: {
    // 输入非空（忽略首尾空格）
    hasKeyword() {
      return this.keyword.trim().length > 0;
    },
    // 有历史时展示最近搜索区块
    showRecent() {
      return this.recentSearches.length > 0;
    },
    // 输入框默认回显 Top 1 热词
    panelPlaceholder() {
      const top = this.hotWords[0];
      return (top && top.keyword) || "请输入你想提问的问题";
    },
  },
  watch: {
    // 实时搜索：输入停止 1s 后发起请求
    keyword(val) {
      // 提交搜索时已立即请求，跳过本次防抖，避免重复发请求
      if (this._skipDebounce) {
        this._skipDebounce = false;
        return;
      }
      this.searched = false;
      clearTimeout(this._searchTimer);
      const w = (val || "").trim();
      if (!w) {
        this.searchResults = [];
        this.resultKeyword = "";
        this.searchId = "";
        return;
      }
      this._searchTimer = setTimeout(() => this.fetchSearchResults(w), SEARCH_DEBOUNCE);
    },
  },
  created() {
    this.recentSearches = this.loadRecent();
    this.getHotWords();
  },
  mounted() {
    const el = this.$refs.input;
    if (el) el.focus();
  },
  methods: {
    img(name) {
      return require(`@/assets/images/${ASSETS}${name}`);
    },
    pickHot(word) {
      // 点击热门词：回显 + 发起搜索（由 submitSearch 统一处理）
      this.submitSearch(word);
    },
    // ===== 热门词 =====
    getHotWords() {
      api
        .getHotWords()
        .then((res) => {
          const { code, data } = res || {};
          if (code !== 0 || !data || !Array.isArray(data.datas)) return;
          this.hotWords = data.datas.filter((it) => it && it.keyword);
        })
        .catch(() => { });
    },
    // ===== 搜索提交与最近搜索历史 =====
    // 回车：新窗口跳转文档搜索页，携带 URL 编码后的关键词 q
    goSearchPage() {
      const w = (this.keyword || "").trim() || (this.hotWords[0] && this.hotWords[0].keyword) || "";
      if (!w) return;
      window.open("https://tushare.pro/document/search?q=" + encodeURIComponent(w), "_blank");
    },
    // 即时搜索（点热词/最近搜索触发，用于面板内展示结果）；搜索记录由结果长度决定，见 fetchSearchResults
    submitSearch(word) {
      // 空输入时使用 placeholder 回显的 Top 1 热词
      const w = (word || "").trim() || (this.hotWords[0] && this.hotWords[0].keyword) || "";
      if (!w) return;
      clearTimeout(this._searchTimer);
      if (this.keyword !== w) {
        this._skipDebounce = true;
        this.keyword = w;
      }
      this.fetchSearchResults(w);
    },
    // 请求搜索结果：source 取当前路由 meta.name
    fetchSearchResults(word) {
      const w = (word || "").trim();
      if (!w) return;
      const seq = (this._reqSeq = (this._reqSeq || 0) + 1);
      const meta = (this.$route && this.$route.meta) || {};
      api
        .getSearchResults({
          keyword: w,
          source: meta.name || "",
          skip: 0,
          limit: 10,
        })
        .then((res) => {
          // 仅采用最后一次请求的结果，避免快速输入时旧响应覆盖新结果
          if (seq !== this._reqSeq) return;
          const { code, data } = res || {};
          // 最近搜索：只要有响应就写入本地，不再依据后端返回条数（0 条也记录）
          this.addRecent(w);
          if (code !== 0 || !data) {
            this.searchResults = [];
            this.searchId = "";
            this.resultKeyword = w;
            this.searched = true;
            return;
          }
          this.searchId = data.search_id || "";
          this.resultKeyword = w;
          this.searchResults = (data.datas || []).map((it) => ({
            id: it.id,
            title: it.title || "",
            desc: this.cleanText(it.content),
            url: it.doc_url || "",
          }));
          this.searched = true;
        })
        .catch(() => {
          if (seq !== this._reqSeq) return;
          this.searchResults = [];
          this.searchId = "";
          this.resultKeyword = w;
          this.searched = true;
        });
    },
    // 富文本片段转纯文本（去 HTML 标签、实体与多余空白），保证高亮基于纯文本
    cleanText(str) {
      return String(str || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\s+/g, " ")
        .trim();
    },
    // 关键词命中拆分：[{t, hit}]，供模板渲染高亮片段
    highlightSegs(text) {
      const src = String(text || "");
      const kw = this.resultKeyword;
      if (!kw) return [{ t: src, hit: false }];
      const lowerSrc = src.toLowerCase();
      const lowerKw = kw.toLowerCase();
      const out = [];
      let i = 0;
      while (i < src.length) {
        const idx = lowerSrc.indexOf(lowerKw, i);
        if (idx === -1) {
          out.push({ t: src.slice(i), hit: false });
          break;
        }
        if (idx > i) out.push({ t: src.slice(i, idx), hit: false });
        out.push({ t: src.slice(idx, idx + kw.length), hit: true });
        i = idx + kw.length;
      }
      return out;
    },
    // 点击最近搜索标签：回显 + 重新搜索
    pickRecent(word) {
      this.submitSearch(word);
    },
    // 点击搜索结果：新开接口文档 + 上报点击行为；0.5s 后收起面板并清空输入
    onResultClick(item) {
      if (item.url) window.open(item.url, "_blank");
      if (this.searchId) {
        api
          .selectSearchResult({ doc_id: item.id, search_id: this.searchId })
          .catch(() => { });
      }
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => {
        this.keyword = "";
        this.$emit("close");
      }, 500);
    },
    // 最近搜索标签展示文案：超过 14 字以「…」省略
    shortRecent(word) {
      const w = String(word || "");
      return w.length > RECENT_SHOW_MAX ? w.slice(0, RECENT_SHOW_MAX) + "…" : w;
    },
    // 写入历史：去重置顶，上限 10 条（超出时丢弃最旧记录）
    addRecent(word) {
      const list = this.recentSearches.filter((item) => item !== word);
      list.unshift(word);
      this.recentSearches = list.slice(0, RECENT_MAX);
      this.saveRecent();
    },
    // 清空全部历史
    clearRecent() {
      this.recentSearches = [];
      this.saveRecent();
    },
    loadRecent() {
      try {
        const raw = localStorage.getItem(RECENT_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter((item) => typeof item === "string" && item) : [];
      } catch (e) {
        return [];
      }
    },
    saveRecent() {
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(this.recentSearches));
      } catch (e) {
        /* 隐私模式等写入失败时静默降级，历史仅在内存中生效 */
      }
    },
    // Figma 榜序色：1 #fc3e4f / 2 #ff7d00 / 3 #ffb44a / 其余 #a2abb9（同 PC）
    hotRankColor(i) {
      return ["#fc3e4f", "#ff7d00", "#ffb44a"][i] || "#a2abb9";
    },
  },
  beforeDestroy() {
    clearTimeout(this._searchTimer);
    clearTimeout(this._closeTimer);
  },
};
</script>

<style lang="scss" scoped>
$font: "Source Han Sans SC";
$brand: #3461ff;
$text-6: #1d2129;
$text-5: #4e5969;
$text-4: #86909c;
$fill-5: #e5e6eb;

.m-search {
  position: fixed;
  top: 64px;
  right: 0;
  bottom: 0;
  left: 0;
  background: #ffffff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  font-family: $font;
  z-index: 40;

  &,
  & * {
    box-sizing: border-box;
  }

  /* 输入栏：胶囊输入框 + 取消，与头部同宽留白节奏 */
  &__bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 16px;
  }

  /* 渐变描边胶囊：同 PC 搜索框描边色系（306.5deg #002bff→#1051f5→#9225ff） */
  &__inputwrap {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    border: 1.5px solid transparent;
    border-radius: 100px;
    background: linear-gradient(#ffffff, #ffffff) padding-box,
      linear-gradient(306.5deg, #002bff 0%, #1051f5 60.73%, #9225ff 100%) border-box;
    height: 40px;
  }

  /* 搜索图标：PNG 蒙版着色（同 PC 实现），固定于输入框左侧 */
  &__icon {
    position: absolute;
    left: 14px;
    width: 16px;
    height: 16px;
    background-color: $text-4;
    -webkit-mask: url("~@/assets/images/home/search_icon_submit.png") no-repeat center / 16px 16px;
    mask: url("~@/assets/images/home/search_icon_submit.png") no-repeat center / 16px 16px;
    pointer-events: none;
    transition: background-color 0.2s ease;
  }

  &__inputwrap--filled &__icon {
    background-color: $text-6;
  }

  &__input {
    border: none;
    outline: none;
    background: transparent;
    padding: 0 38px 0 38px;
    width: 100%;
    height: 100%;
    line-height: 22px;
    letter-spacing: 0;
    color: $text-6;
    font-family: $font;
    font-size: 14px;
    caret-color: $text-6;

    &::placeholder {
      color: $text-4;
    }
  }

  &__clear {
    position: absolute;
    right: 14px;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  &__cancel {
    flex-shrink: 0;
    border: none;
    outline: none;
    background: transparent;
    padding: 8px 0 8px 8px;
    line-height: 22px;
    letter-spacing: 0;
    color: $text-6;
    font-family: $font;
    font-size: 14px;
    cursor: pointer;
  }

  &__body {
    padding: 8px 16px 24px;
  }

  &__section {
    margin-top: 16px;
  }

  &__sec-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 22px;
  }

  &__sec-title {
    display: flex;
    align-items: center;

    img {
      width: 13px;
      height: 13px;
    }

    span {
      margin-left: 4px;
      line-height: 22px;
      letter-spacing: 0;
      color: $text-5;
      font-size: 14px;
    }
  }

  /* 清空按钮：垃圾桶 16x16 + 文案（同 PC 最近搜索头） */
  &__clearall {
    display: flex;
    align-items: center;
    cursor: pointer;

    img {
      width: 16px;
      height: 16px;
    }

    span {
      margin-left: 3px;
      line-height: 20px;
      letter-spacing: 0;
      color: $text-5;
      font-size: 14px;
    }
  }

  /* 标签云：描边 1px #e5e6eb r4，同 PC 标签规范 */
  &__tags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 12px;
    gap: 10px;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    border: 1px solid $fill-5;
    border-radius: 4px;
    padding: 5px 12px;
    max-width: 100%;
    min-height: 30px;
    line-height: 20px;
    letter-spacing: 0;
    color: $text-5;
    font-size: 13px;
    white-space: normal;
    word-break: break-all;
    cursor: pointer;

    &:active {
      border-color: $brand;
      color: $brand;
    }
  }

  &__hot-list {
    margin: 12px 0 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      height: 24px;
      cursor: pointer;

      & + li {
        margin-top: 12px;
      }

      em {
        width: 10px;
        line-height: 24px;
        letter-spacing: 0;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
      }

      span {
        margin-left: 12px;
        line-height: 24px;
        letter-spacing: 0;
        color: $text-6;
        font-size: 14px;
      }

      &:active span {
        color: $brand;
      }
    }
  }

  /* 搜索结果列表（同 PC：标题 + 双行描述） */
  &__results {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
    /* 结果区高度固定：超出时在结果区内纵向滚动，不撑开整体布局 */
    max-height: 60vh;
    overflow-y: auto;
    overscroll-behavior: contain;

    li {
      cursor: pointer;

      & + li {
        margin-top: 16px;
      }

      &:active .m-search__result-title {
        color: $brand;
      }
    }
  }

  &__result-title {
    margin: 0;
    line-height: 22px;
    letter-spacing: 0;
    color: $text-6;
    font-size: 14px;
    transition: color 0.2s ease;
  }

  &__result-desc {
    display: -webkit-box;
    overflow: hidden;
    margin: 4px 0 0;
    text-overflow: ellipsis;
    line-height: 20px;
    letter-spacing: 0;
    color: $text-4;
    font-size: 12px;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  /* 关键词命中高亮：品牌蓝 + 中等字重（同 PC） */
  &__hl {
    color: $brand;
    font-weight: 500;
  }

  &__empty {
    padding-top: 40px;
    text-align: center;
    line-height: 22px;
    letter-spacing: 0;
    color: $text-4;
    font-size: 14px;
  }
}
</style>
